import React, { useState, useEffect, useCallback } from "react";
import {
  getDashboardOnboardedDevices,
  getDashboardSalesIncome,
  getDashboardStats,
  getDashboardSalesProducts,
  getDashboardTopSellingProducts,
} from "../actions/DashboardAction";
import Overview from "../components/dashboard/Overview";
import Header from "../components/dashboard/Header";
import StockProduct from "../components/dashboard/StockProduct";
import Loading from "../components/layout/Loading";
import { BRANDLIST } from "../constants/FetchConstant";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [salesTimeline, setSalesTimeline] = useState({});
  const [onboardedDevices, setOnboardedDevices] = useState({});
  const [salesProducts, setSalesProducts] = useState({});
  const [topSellingProducts, setTopSellingProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    stats: null,
    salesTimeline: null,
    onboardedDevices: null,
    salesProducts: null,
    topSellingProducts: null,
  });

  const date = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [
          statsRes,
          salesTimelineRes,
          onboardedDevicesRes,
          salesProductsRes,
          topSellingProductsRes,
        ] = await Promise.allSettled([
          getDashboardStats(date),
          getDashboardSalesIncome(date),
          getDashboardOnboardedDevices(date),
          getDashboardSalesProducts(date),
          getDashboardTopSellingProducts(date),
        ]);

        const newErrors = {
          stats: null,
          salesTimeline: null,
          onboardedDevices: null,
          salesProducts: null,
          topSellingProducts: null,
        };

        const toastMessages = [];

        const getSettledErrorMessage = (result, fallbackMessage) => {
          if (result.status === "rejected") {
            return result.reason?.message || fallbackMessage;
          }
          return result.value?.message || fallbackMessage;
        };

        if (statsRes.status === "fulfilled" && statsRes.value.success) {
          setStats(statsRes.value.data);
        } else {
          const msg = getSettledErrorMessage(
            statsRes,
            "Failed to fetch dashboard stats.",
          );
          newErrors.stats = msg;
          toastMessages.push(msg);
        }

        if (
          salesTimelineRes.status === "fulfilled" &&
          salesTimelineRes.value.success
        ) {
          setSalesTimeline(salesTimelineRes.value.data);
        } else {
          const msg = getSettledErrorMessage(
            salesTimelineRes,
            "Failed to fetch sales income data.",
          );
          newErrors.salesTimeline = msg;
          toastMessages.push(msg);
        }

        if (
          onboardedDevicesRes.status === "fulfilled" &&
          onboardedDevicesRes.value.success
        ) {
          setOnboardedDevices(onboardedDevicesRes.value.data);
        } else {
          const msg = getSettledErrorMessage(
            onboardedDevicesRes,
            "Failed to fetch onboarded devices.",
          );
          newErrors.onboardedDevices = msg;
          toastMessages.push(msg);
        }

        if (
          salesProductsRes.status === "fulfilled" &&
          salesProductsRes.value.success
        ) {
          setSalesProducts(salesProductsRes.value.data);
        } else {
          const msg = getSettledErrorMessage(
            salesProductsRes,
            "Failed to fetch sales products.",
          );
          newErrors.salesProducts = msg;
          toastMessages.push(msg);
        }

        if (
          topSellingProductsRes.status === "fulfilled" &&
          topSellingProductsRes.value.success
        ) {
          setTopSellingProducts(topSellingProductsRes.value.data);
        } else {
          const msg = getSettledErrorMessage(
            topSellingProductsRes,
            "Failed to fetch top selling products.",
          );
          newErrors.topSellingProducts = msg;
          toastMessages.push(msg);
        }

        [...new Set(toastMessages)].forEach((msg) => toast.error(msg));
        setErrors(newErrors);
      } catch (err) {
        toast.error("Something went wrong. Please try again.");
        setErrors({
          stats: err.message,
          salesTimeline: err.message,
          onboardedDevices: err.message,
          salesProducts: err.message,
          topSellingProducts: err.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const refetchSalesProducts = useCallback(
    async (brandId) => {
      const res = await getDashboardSalesProducts(date, brandId);
      if (res.success) {
        setSalesProducts(res.data);
      } else {
        toast.error(res.message || "Failed to fetch sales products.");
      }
    },
    [date],
  );

  const refetchTopSellingProducts = useCallback(
    async (brandId) => {
      const res = await getDashboardTopSellingProducts(date, brandId);
      if (res.success) {
        setTopSellingProducts(res.data);
      } else {
        toast.error(res.message || "Failed to fetch top selling products.");
      }
    },
    [date],
  );

  if (loading)
    return (
      <div className="text-center text-gray-500">
        <Loading />
      </div>
    );

  if (Object.values(errors).some((e) => e !== null))
    return (
      <div className="text-red-500 text-center">
        {errors.stats && <p>Stats Error: {errors.stats}</p>}
        {errors.salesTimeline && (
          <p>Sales Timeline Error: {errors.salesTimeline}</p>
        )}
        {errors.onboardedDevices && (
          <p>Onboarded Devices Error: {errors.onboardedDevices}</p>
        )}
        {errors.salesProducts && (
          <p>Sales Products Error: {errors.salesProducts}</p>
        )}
        {errors.topSellingProducts && (
          <p>Top Selling Products Error: {errors.topSellingProducts}</p>
        )}
      </div>
    );

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <div className=" mx-auto">
        <Header stats={stats} />
        <Overview salesTimeline={salesTimeline} />
        <StockProduct
          brandNames={BRANDLIST}
          salesProducts={salesProducts}
          topSellingProductsData={topSellingProducts}
          onboardedDevices={onboardedDevices}
          brands={[]}
          onBrandSalesChange={refetchSalesProducts}
          onBrandTopSellingChange={refetchTopSellingProducts}
        />
      </div>
    </div>
  );
};

export default Dashboard;
